import React, { useContext, useState, useEffect } from 'react';
import { ethers } from 'ethers';
import { Web3Context } from '../../context/Web3Context';
import CONTRACT_ADDRESSES from '../../contracts/addresses';
import { formatUSDT, formatDate, shortenAddress } from '../../utils/formatters';
import { PACKAGE_NAMES, PACKAGE_PRICES, CEILING_LIMITS } from '../../utils/constants';
import { checkUserAllTiers, saveEligibility, getEligibleList, removeUserFromTier, clearEligibility } from '../../utils/rewardEligibility';
import './Admin.css';

function AdminSection({ title, children, defaultOpen = false }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="admin-section">
      <div className="admin-section-header" onClick={() => setOpen(!open)}>
        <span className="admin-section-title">{title}</span>
        <span className={`admin-arrow ${open ? 'admin-arrow-open' : ''}`}>&#9662;</span>
      </div>
      {open && <div className="admin-section-body">{children}</div>}
    </div>
  );
}

function AdminInput({ label, value, onChange, placeholder, type = 'text' }) {
  return (
    <div className="admin-field">
      <label className="admin-label">{label}</label>
      <input className="admin-input" type={type} value={value} onChange={e => onChange(e.target.value)} placeholder={placeholder} />
    </div>
  );
}

function AdminBtn({ onClick, disabled, children, danger }) {
  return (
    <button className={`admin-btn ${danger ? 'admin-btn-danger' : ''}`} onClick={onClick} disabled={disabled}>
      {children}
    </button>
  );
}

function StatusRow({ label, ok }) {
  return (
    <div className="admin-status-row">
      <span className={`admin-status-dot ${ok ? 'admin-dot-ok' : 'admin-dot-err'}`} />
      <span className="admin-status-label">{label}</span>
      <span className={`admin-status-text ${ok ? '' : 'admin-status-err'}`}>{ok ? 'Connected' : 'Mismatch'}</span>
    </div>
  );
}

export default function Admin({ notify }) {
  const { contracts, readContracts, isOwner } = useContext(Web3Context);
  const [contractStatus, setContractStatus] = useState(null);
  const [processing, setProcessing] = useState(null);
  const [royaltyData, setRoyaltyData] = useState(null);
  const [dividendData, setDividendData] = useState(null);
  const [rewardTiers, setRewardTiers] = useState([]);
  const [rewardEligibleByTier, setRewardEligibleByTier] = useState({});
  const [selectedRewardTier, setSelectedRewardTier] = useState('0');
  const [rewardLoading, setRewardLoading] = useState(false);
  const [newAdminAddr, setNewAdminAddr] = useState('');

  // Lookup
  const [lookupId, setLookupId] = useState('');
  const [lookupData, setLookupData] = useState(null);
  const [lookupLoading, setLookupLoading] = useState(false);

  // Privileged claim IDs - stored in localStorage so owner sets once, admin uses
  const [privClaimSavedIds, setPrivClaimSavedIds] = useState(() => {
    try { return localStorage.getItem('uvw_priv_claim_ids') || ''; } catch { return ''; }
  });

  // Form states
  const [regWallet, setRegWallet] = useState('');
  const [regRef, setRegRef] = useState('');
  const [regPkgId, setRegPkgId] = useState('0');
  const [blockUserId, setBlockUserId] = useState('');
  const [retopUserId, setRetopUserId] = useState('');
  const [retopPkgId, setRetopPkgId] = useState('0');
  const [eliteRoundAmt, setEliteRoundAmt] = useState('');
  const [globalRoundAmt, setGlobalRoundAmt] = useState('');
  const [eligUserId, setEligUserId] = useState('');
  const [walletAddr, setWalletAddr] = useState('');
  const [emergAmt, setEmergAmt] = useState('');
  const [defaultRefId, setDefaultRefId] = useState('');
  const [usdtAddr, setUsdtAddr] = useState('');
  const [setContractAddr, setSetContractAddr] = useState('');
  const [rewardTierId, setRewardTierId] = useState('');
  const [rewardName, setRewardName] = useState('');
  const [rewardDesc, setRewardDesc] = useState('');
  const [rewardDirects, setRewardDirects] = useState('');
  const [rewardLevel, setRewardLevel] = useState('');
  const [rewardMembers, setRewardMembers] = useState('');
  const [markGivenTier, setMarkGivenTier] = useState('');
  const [markGivenUser, setMarkGivenUser] = useState('');
  const [privClaimIds, setPrivClaimIds] = useState('');

  useEffect(() => {
    if (!readContracts.core) return;
    let cancelled = false;
    async function loadStatus() {
      try {
        const [
          corePoolsAddr, coreMatrixAddr, poolsCoreAddr, matrixCoreAddr, viewsCoreAddr, viewsPoolsAddr,
          contractBal, totalUsersCount,
          eliteAccum, globalAccum, eliteEligCount, globalEligCount, eliteRound, globalRound,
          dividendInfo
        ] = await Promise.all([
          readContracts.core.poolsContract(),
          readContracts.core.matrixContract(),
          readContracts.pools.coreContract(),
          readContracts.matrix.coreContract(),
          readContracts.views.coreContract(),
          readContracts.views.poolsContract(),
          readContracts.core.getContractBalance(),
          readContracts.core.totalUsers().catch(() => ethers.BigNumber.from(0)),
          readContracts.pools.eliteRoyaltyAccumulation(),
          readContracts.pools.globalRoyaltyAccumulation(),
          readContracts.pools.eliteRoyaltyEligibleCount(),
          readContracts.pools.globalRoyaltyEligibleCount(),
          readContracts.pools.currentEliteRound(),
          readContracts.pools.currentGlobalRound(),
          readContracts.views.getDividendPoolInfo().catch(() => null)
        ]);
        if (cancelled) return;
        setContractStatus({
          totalUsers: totalUsersCount.toNumber(),
          corePoolsOk: corePoolsAddr.toLowerCase() === CONTRACT_ADDRESSES.UVW_Pools.toLowerCase(),
          coreMatrixOk: coreMatrixAddr.toLowerCase() === CONTRACT_ADDRESSES.UVW_Matrix.toLowerCase(),
          poolsCoreOk: poolsCoreAddr.toLowerCase() === CONTRACT_ADDRESSES.UVW_Core.toLowerCase(),
          matrixCoreOk: matrixCoreAddr.toLowerCase() === CONTRACT_ADDRESSES.UVW_Core.toLowerCase(),
          viewsCoreOk: viewsCoreAddr.toLowerCase() === CONTRACT_ADDRESSES.UVW_Core.toLowerCase(),
          viewsPoolsOk: viewsPoolsAddr.toLowerCase() === CONTRACT_ADDRESSES.UVW_Pools.toLowerCase(),
          contractBal
        });
        const eliteElig = eliteEligCount.toNumber();
        const globalElig = globalEligCount.toNumber();
        setRoyaltyData({
          eliteAccum, globalAccum,
          eliteEligible: eliteElig, globalEligible: globalElig,
          elitePerUser: eliteElig > 0 ? eliteAccum.div(eliteElig) : ethers.BigNumber.from(0),
          globalPerUser: globalElig > 0 ? globalAccum.div(globalElig) : ethers.BigNumber.from(0),
          eliteRound: eliteRound.toNumber(), globalRound: globalRound.toNumber()
        });
        if (dividendInfo) {
          setDividendData({
            totalAccum: dividendInfo.totalAccum,
            totalShares: dividendInfo.totalShares
          });
        }

        // Reward tiers
        try {
          const tc = await readContracts.pools.rewardTierCount();
          const tierCount = tc.toNumber();
          const tierPs = [];
          for (let i = 0; i < tierCount; i++) tierPs.push(readContracts.pools.rewardTiers(i));
          const tiers = await Promise.all(tierPs);
          if (!cancelled) setRewardTiers(tiers.map((t, i) => ({
            id: i, name: t.name, description: t.description,
            directsRequired: t.directsRequired.toNumber(),
            levelRequired: t.levelRequired.toNumber(),
            membersRequired: t.membersRequired.toNumber()
          })));
        } catch { /* silent */ }
      } catch (err) { if (!cancelled) notify.error('Failed to load admin data: ' + (err.reason || err.message)); }
    }
    loadStatus();
    return () => { cancelled = true; };
  }, [readContracts.core]);

  async function exec(label, fn) {
    setProcessing(label);
    try {
      const loadId = notify.loading(label + '...');
      const tx = await fn();
      await tx.wait();
      notify.removeNotification(loadId);
      notify.success(label + ' successful!');
    } catch (err) {
      notify.error(label + ' failed: ' + (err.reason || err.message));
    }
    setProcessing(null);
  }

  // Loads cached eligibility from localStorage and enriches with on-chain "given" status
  async function loadRewardEligibleUsers() {
    const tierId = parseInt(selectedRewardTier);
    setRewardLoading(true);
    try {
      const cached = getEligibleList(tierId);
      if (cached.length === 0) {
        setRewardEligibleByTier(prev => ({ ...prev, [tierId]: [] }));
        notify.info('No cached eligible users. Run "Scan All" or wait for users to be discovered via lookups.');
        setRewardLoading(false);
        return;
      }
      // Verify still eligible + check given status
      const checks = await Promise.all(cached.map(async (entry) => {
        const [tiers, given] = await Promise.all([
          checkUserAllTiers(readContracts, entry.userId),
          readContracts.pools.isUserRewardGiven(tierId, entry.userId).catch(() => false)
        ]);
        const stillEligible = tiers[tierId] && tiers[tierId].eligible;
        // Update storage with fresh data
        saveEligibility(entry.userId, tiers);
        return stillEligible ? {
          userId: entry.userId,
          count: tiers[tierId].count,
          required: tiers[tierId].required,
          given
        } : null;
      }));
      const enriched = checks.filter(c => c !== null);
      setRewardEligibleByTier(prev => ({ ...prev, [tierId]: enriched }));
      notify.success(`${enriched.length} currently eligible (verified)`);
    } catch (err) {
      notify.error('Failed to load: ' + (err.reason || err.message));
    }
    setRewardLoading(false);
  }

  // Scans all users on-chain (heavy, uses 2 reads per user)
  async function scanAllUsersForRewards() {
    setRewardLoading(true);
    try {
      const totalUsersBN = await readContracts.core.totalUsers();
      const total = totalUsersBN.toNumber();
      const loadId = notify.loading(`Scanning ${total} users...`);
      const allUsers = [];
      const batchSize = 100;
      for (let offset = 0; offset < total; offset += batchSize) {
        const limit = Math.min(batchSize, total - offset);
        const idPromises = [];
        for (let i = offset; i < offset + limit; i++) {
          idPromises.push(readContracts.core.allUserIds(i).catch(() => null));
        }
        const ids = await Promise.all(idPromises);
        for (const id of ids) {
          if (id) {
            const n = id.toNumber ? id.toNumber() : Number(id);
            if (n > 0) allUsers.push(n);
          }
        }
      }
      // Check eligibility per user (2 reads each, batched)
      const groupSize = 10;
      let eligibleAny = 0;
      for (let g = 0; g < allUsers.length; g += groupSize) {
        const group = allUsers.slice(g, g + groupSize);
        const checks = await Promise.all(group.map(uid => checkUserAllTiers(readContracts, uid)));
        checks.forEach((tiers, idx) => {
          saveEligibility(group[idx], tiers);
          if (tiers.some(t => t.eligible)) eligibleAny++;
        });
      }
      notify.removeNotification(loadId);
      notify.success(`Scan complete. ${eligibleAny} eligible users saved.`);
      // Reload current tier display
      await loadRewardEligibleUsers();
    } catch (err) {
      notify.error('Scan failed: ' + (err.reason || err.message));
    }
    setRewardLoading(false);
  }

  async function markAsGiven(tierId, userId) {
    setProcessing('markGiven-' + userId);
    try {
      const tx = await contracts.pools.markRewardGiven(tierId, userId);
      await tx.wait();
      notify.success(`Marked tier ${tierId} as given for user ${userId}`);
      removeUserFromTier(tierId, userId); // remove from local cache
      // Update display
      setRewardEligibleByTier(prev => {
        const list = (prev[tierId] || []).filter(u => u.userId !== userId);
        return { ...prev, [tierId]: list };
      });
    } catch (err) {
      notify.error('Failed: ' + (err.reason || err.message));
    }
    setProcessing(null);
  }

  async function handleLookup() {
    if (!lookupId) return;
    setLookupLoading(true); setLookupData(null);
    try {
      const uid = parseInt(lookupId);
      const ceilingCalls = [];
      for (let i = 0; i < 7; i++) {
        ceilingCalls.push(readContracts.core.checkCeilingAvailable(uid, i, ethers.constants.MaxUint256).catch(() => [false, ethers.BigNumber.from(0), ethers.BigNumber.from(0)]));
      }
      const [info, dash, breakdown, directs, eliteElig, globalElig, acts, rewardCounters, rewardStatusData, ...ceilingResults] = await Promise.all([
        readContracts.views.getUserInfo(uid),
        readContracts.views.getUserDashboard(uid),
        readContracts.views.getUserIncomeBreakdown(uid),
        readContracts.core.getDirectReferralsList(uid).catch(() => []),
        readContracts.pools.eliteRoyaltyEligible(uid),
        readContracts.pools.globalRoyaltyEligible(uid),
        readContracts.views.getUserActivities(uid, 0, 50).catch(() => ({ types: [], amounts: [], timestamps: [] })),
        readContracts.views.getUserRewardCounters(uid).catch(() => ({ fpDirects: ethers.BigNumber.from(0), fpLevelCounts: [0, 0, 0, 0, 0] })),
        readContracts.views.getUserRewardStatus(uid).catch(() => ({ eligible: [], given: [] })),
        ...ceilingCalls
      ]);
      const ceilingData = ceilingResults.map((res, i) => {
        const limitWei = ethers.utils.parseUnits(CEILING_LIMITS[i].toString(), 18);
        const remaining = res[1];
        const used = limitWei.sub(remaining.gt(limitWei) ? limitWei : remaining);
        return { used, limit: limitWei, remaining, active: info.packageActive[i] };
      });
      const retopups = [];
      for (let i = 0; i < acts.types.length; i++) {
        if (acts.types[i] === 9) retopups.push(acts.timestamps[i]);
      }
      setLookupData({
        userId: uid, wallet: info.wallet, referrerId: info.referrerId.toNumber(),
        joinTime: info.joinTime, packageActive: info.packageActive,
        totalDirectRefs: info.totalDirectRefs.toNumber(),
        totalIncomeEarned: info.totalIncomeEarned, userType: info.userType, blocked: info.blocked,
        totalIncome: dash.totalIncome, claimableLevel: dash.claimableLevel,
        claimableAutoLevel: dash.claimableAutoLevel, claimableElite: dash.claimableElite,
        claimableGlobal: dash.claimableGlobal, claimableDividend: dash.claimableDividend,
        boardIncome: breakdown.boardIncome, directIncome: breakdown.directIncome,
        levelIncome: breakdown.levelIncome, autoLevelIncome: breakdown.autoLevelIncome,
        royaltyIncome: breakdown.royaltyIncome, dividendIncome: breakdown.dividendIncome,
        eliteEligible: eliteElig, globalEligible: globalElig,
        directs: directs.map(d => d.toNumber ? d.toNumber() : Number(d)).filter(d => d > 0),
        ceilingData,
        retopupDates: retopups.map(t => t.toNumber ? t.toNumber() : Number(t)).filter(t => t > 0),
        fpDirects: rewardCounters.fpDirects.toNumber ? rewardCounters.fpDirects.toNumber() : 0,
        fpLevelCounts: rewardCounters.fpLevelCounts ? rewardCounters.fpLevelCounts.map(c => c.toNumber ? c.toNumber() : 0) : [],
        rewardEligible: Array.isArray(rewardStatusData.eligible) ? rewardStatusData.eligible : [],
        rewardGiven: Array.isArray(rewardStatusData.given) ? rewardStatusData.given : [],
        rewardProgress: [] // populated below
      });

      // Compute reward eligibility from on-chain counters (fast, 2 reads)
      try {
        const tierResults = await checkUserAllTiers(readContracts, uid);
        setLookupData(prev => prev ? { ...prev, rewardProgress: tierResults } : prev);
        saveEligibility(uid, tierResults); // save to localStorage
      } catch { /* silent */ }
    } catch (err) { notify.error('Lookup failed: ' + (err.reason || err.message)); }
    setLookupLoading(false);
  }

  async function handleChangeAdmin() {
    if (!newAdminAddr) { notify.warning('Enter admin address'); return; }
    setProcessing('changeAdmin');
    try {
      const loadId = notify.loading('Changing admin on all contracts...');
      const tx1 = await contracts.core.setAdmin(newAdminAddr); await tx1.wait();
      const tx2 = await contracts.pools.setAdmin(newAdminAddr); await tx2.wait();
      const tx3 = await contracts.matrix.setAdmin(newAdminAddr); await tx3.wait();
      notify.removeNotification(loadId);
      notify.success('Admin changed on all 3 contracts!');
    } catch (err) {
      notify.error('Change admin failed: ' + (err.reason || err.message));
    }
    setProcessing(null);
  }

  function savePrivClaimIds() {
    try {
      localStorage.setItem('uvw_priv_claim_ids', privClaimIds);
      setPrivClaimSavedIds(privClaimIds);
      notify.success('Privileged user IDs saved!');
    } catch { notify.error('Failed to save'); }
  }

  async function handlePrivClaim() {
    setProcessing('privClaim');
    try {
      const source = isOwner ? privClaimIds : privClaimSavedIds;
      const ids = source.split(',').map(s => parseInt(s.trim())).filter(n => n > 0);
      if (ids.length === 0) {
        notify.warning(isOwner ? 'Enter user IDs (comma separated)' : 'No saved IDs. Owner must set them first.');
        setProcessing(null); return;
      }
      const loadId = notify.loading(`Claiming for ${ids.length} privileged users...`);
      const tx = await contracts.pools.adminClaimForPrivileged(ids);
      await tx.wait();
      notify.removeNotification(loadId);
      notify.success('Privileged claims processed!');
    } catch (err) { notify.error('Claim failed: ' + (err.reason || err.message)); }
    setProcessing(null);
  }

  return (
    <div className="admin-page">
      <h2 className="page-title">Admin Panel</h2>
      <div className="admin-top-row">
        <span className="admin-role-badge">{isOwner ? 'Owner' : 'Admin'}</span>
        {contractStatus && <span className="admin-users-badge">Total Users: {contractStatus.totalUsers}</span>}
      </div>

      {/* ===== OVERVIEW (always visible) ===== */}
      <AdminSection title="Overview" defaultOpen={true}>
        <div className="admin-overview-grid">
          <div className="admin-overview-card">
            <span className="admin-overview-label">Total Users</span>
            <span className="admin-overview-val" style={{ color: '#00e5ff' }}>{contractStatus?.totalUsers || 0}</span>
          </div>
          <div className="admin-overview-card">
            <span className="admin-overview-label">Contract Balance</span>
            <span className="admin-overview-val" style={{ color: '#ffd700' }}>${formatUSDT(contractStatus?.contractBal || 0)}</span>
          </div>
          {isOwner && (
            <>
              <div className="admin-overview-card">
                <span className="admin-overview-label">Elite Pool</span>
                <span className="admin-overview-val" style={{ color: '#ec4899' }}>${formatUSDT(royaltyData?.eliteAccum || 0)}</span>
              </div>
              <div className="admin-overview-card">
                <span className="admin-overview-label">Global Pool</span>
                <span className="admin-overview-val" style={{ color: '#f97316' }}>${formatUSDT(royaltyData?.globalAccum || 0)}</span>
              </div>
              <div className="admin-overview-card">
                <span className="admin-overview-label">Elite Eligible</span>
                <span className="admin-overview-val">{royaltyData?.eliteEligible || 0}</span>
              </div>
              <div className="admin-overview-card">
                <span className="admin-overview-label">Global Eligible</span>
                <span className="admin-overview-val">{royaltyData?.globalEligible || 0}</span>
              </div>
              <div className="admin-overview-card">
                <span className="admin-overview-label">Elite Round</span>
                <span className="admin-overview-val">{royaltyData?.eliteRound || 0}</span>
              </div>
            </>
          )}
        </div>

        {dividendData && (
          <>
            <div className="admin-sub-title" style={{ marginTop: 12 }}>Users Per Package</div>
            <div className="admin-dividend-grid">
              {PACKAGE_NAMES.map((name, i) => {
                // dividendTotalShares = active users + 1. So users = shares - 1
                const shares = dividendData.totalShares[i];
                const userCount = shares.gt ? Math.max(shares.toNumber() - 1, 0) : Math.max(Number(shares) - 1, 0);
                return (
                  <div key={i} className="admin-dividend-item">
                    <span className="admin-dividend-pkg">{name}</span>
                    {isOwner && <span className="admin-dividend-amt">${formatUSDT(dividendData.totalAccum[i])}</span>}
                    <span className="admin-dividend-shares" style={{ color: '#06b6d4', fontWeight: 700 }}>{userCount} users</span>
                  </div>
                );
              })}
            </div>
          </>
        )}
      </AdminSection>

      {/* ===== LOOKUP USER ===== */}
      <AdminSection title="Lookup User">
        <div className="claim-input-row" style={{ marginBottom: 10 }}>
          <AdminInput label="User ID" value={lookupId} onChange={setLookupId} placeholder="Enter User ID" type="number" />
          <AdminBtn onClick={handleLookup} disabled={lookupLoading || !lookupId}>
            {lookupLoading ? 'Loading...' : 'Lookup'}
          </AdminBtn>
        </div>
        {lookupData && (
          <div className="lookup-result">
            <div className="lookup-header">
              <span className="lookup-uid">ID: {lookupData.userId}</span>
              <span className={`lookup-type ${lookupData.userType === 1 ? 'lookup-priv' : ''}`}>
                {lookupData.userType === 1 ? 'Privileged' : lookupData.userType === 2 ? 'Virtual' : 'Normal'}
              </span>
              {lookupData.blocked && <span className="lookup-blocked">BLOCKED</span>}
            </div>
            <div className="lookup-grid">
              <div className="lookup-item"><span className="lookup-label">Wallet</span><span className="lookup-val">{shortenAddress(lookupData.wallet)}</span></div>
              <div className="lookup-item"><span className="lookup-label">Referrer</span><span className="lookup-val">{lookupData.referrerId}</span></div>
              <div className="lookup-item"><span className="lookup-label">Joined</span><span className="lookup-val">{formatDate(lookupData.joinTime)}</span></div>
              <div className="lookup-item"><span className="lookup-label">Direct Refs</span><span className="lookup-val">{lookupData.totalDirectRefs}</span></div>
            </div>
            <div className="lookup-sub-title">Packages &amp; Ceiling</div>
            <div className="lookup-ceiling-list">
              {PACKAGE_NAMES.map((name, i) => {
                const c = lookupData.ceilingData[i];
                const usedNum = parseFloat(formatUSDT(c.used).replace(/,/g, ''));
                const limitNum = parseFloat(formatUSDT(c.limit).replace(/,/g, ''));
                const pct = limitNum > 0 ? Math.min((usedNum / limitNum) * 100, 100) : 0;
                return (
                  <div key={i} className={`lookup-ceil-row ${c.active ? 'lookup-ceil-active' : ''}`}>
                    <div className="lookup-ceil-head">
                      <span className={`lookup-pkg ${c.active ? 'lookup-pkg-on' : ''}`}>{name}</span>
                      <span className="lookup-ceil-pct" style={{ color: pct >= 100 ? '#ef4444' : pct >= 75 ? '#f59e0b' : '#34d399' }}>{pct.toFixed(0)}%</span>
                    </div>
                    <div className="lookup-ceil-bar">
                      <div className="lookup-ceil-fill" style={{ width: `${pct}%`, background: pct >= 100 ? '#ef4444' : pct >= 75 ? '#f59e0b' : '#34d399' }} />
                    </div>
                    <div className="lookup-ceil-nums">
                      <span>Used: ${formatUSDT(c.used)}</span>
                      <span>Limit: ${formatUSDT(c.limit)}</span>
                      <span>Left: ${formatUSDT(c.remaining)}</span>
                    </div>
                  </div>
                );
              })}
            </div>
            {lookupData.retopupDates.length > 0 && (
              <>
                <div className="lookup-sub-title">Retopup History</div>
                <div className="lookup-retopup-list">
                  {lookupData.retopupDates.map((ts, i) => (
                    <span key={i} className="lookup-retopup-item">{formatDate(ts)}</span>
                  ))}
                </div>
              </>
            )}
            <div className="lookup-sub-title">Income</div>
            <div className="lookup-grid">
              <div className="lookup-item"><span className="lookup-label">Total Earned</span><span className="lookup-val" style={{ color: '#ffd700' }}>${formatUSDT(lookupData.totalIncome)}</span></div>
              <div className="lookup-item"><span className="lookup-label">Board</span><span className="lookup-val">${formatUSDT(lookupData.boardIncome)}</span></div>
              <div className="lookup-item"><span className="lookup-label">Direct</span><span className="lookup-val">${formatUSDT(lookupData.directIncome)}</span></div>
              <div className="lookup-item"><span className="lookup-label">Level</span><span className="lookup-val">${formatUSDT(lookupData.levelIncome)}</span></div>
              <div className="lookup-item"><span className="lookup-label">Auto Level</span><span className="lookup-val">${formatUSDT(lookupData.autoLevelIncome)}</span></div>
              <div className="lookup-item"><span className="lookup-label">Royalty</span><span className="lookup-val">${formatUSDT(lookupData.royaltyIncome)}</span></div>
              <div className="lookup-item"><span className="lookup-label">Dividend</span><span className="lookup-val">${formatUSDT(lookupData.dividendIncome)}</span></div>
            </div>
            <div className="lookup-sub-title">Claimable</div>
            <div className="lookup-grid">
              <div className="lookup-item"><span className="lookup-label">Level</span><span className="lookup-val" style={{ color: '#34d399' }}>${formatUSDT(lookupData.claimableLevel)}</span></div>
              <div className="lookup-item"><span className="lookup-label">Auto Level</span><span className="lookup-val" style={{ color: '#34d399' }}>${formatUSDT(lookupData.claimableAutoLevel)}</span></div>
              <div className="lookup-item"><span className="lookup-label">Elite</span><span className="lookup-val" style={{ color: '#34d399' }}>${formatUSDT(lookupData.claimableElite)}</span></div>
              <div className="lookup-item"><span className="lookup-label">Global</span><span className="lookup-val" style={{ color: '#34d399' }}>${formatUSDT(lookupData.claimableGlobal)}</span></div>
              <div className="lookup-item"><span className="lookup-label">Dividend</span><span className="lookup-val" style={{ color: '#34d399' }}>${formatUSDT(lookupData.claimableDividend)}</span></div>
            </div>
            <div className="lookup-sub-title">Eligibility &amp; Reward Counters</div>
            <div className="lookup-grid">
              <div className="lookup-item"><span className="lookup-label">Elite Pool</span><span className="lookup-val" style={{ color: lookupData.eliteEligible ? '#34d399' : '#ef4444' }}>{lookupData.eliteEligible ? 'Yes' : 'No'}</span></div>
              <div className="lookup-item"><span className="lookup-label">Global Pool</span><span className="lookup-val" style={{ color: lookupData.globalEligible ? '#34d399' : '#ef4444' }}>{lookupData.globalEligible ? 'Yes' : 'No'}</span></div>
              <div className="lookup-item"><span className="lookup-label">Full Pkg Directs</span><span className="lookup-val">{lookupData.fpDirects}</span></div>
              {lookupData.fpLevelCounts.map((c, i) => (
                <div key={i} className="lookup-item"><span className="lookup-label">FP L{i + 1}</span><span className="lookup-val">{c}</span></div>
              ))}
            </div>

            {rewardTiers.length > 0 && (
              <>
                <div className="lookup-sub-title">Reward Tiers Status</div>
                <div className="lookup-rewards-list">
                  {rewardTiers.map((tier, i) => {
                    const progress = (lookupData.rewardProgress || [])[i];
                    const isComputing = !progress;
                    const eligible = progress && progress.eligible;
                    const given = lookupData.rewardGiven[i];
                    let status, color;
                    if (given) { status = 'Given'; color = '#a855f7'; }
                    else if (isComputing) { status = 'Checking...'; color = 'rgba(255,255,255,0.4)'; }
                    else if (eligible) { status = 'Eligible'; color = '#34d399'; }
                    else { status = 'Locked'; color = 'rgba(255,255,255,0.3)'; }
                    return (
                      <div key={i} className="lookup-reward-row">
                        <div style={{ flex: 1 }}>
                          <span className="lookup-reward-name">{tier.name}</span>
                          {progress && (
                            <span style={{ display: 'block', fontSize: 11, color: '#00e5ff', fontWeight: 700, marginTop: 2 }}>
                              {progress.count}/{progress.required}
                            </span>
                          )}
                        </div>
                        <span className="lookup-reward-status" style={{ color, borderColor: color + '40', background: color + '15' }}>{status}</span>
                      </div>
                    );
                  })}
                </div>
              </>
            )}
            <div className="lookup-sub-title">Direct Referrals ({lookupData.directs.length})</div>
            {lookupData.directs.length > 0 ? (
              <div className="lookup-directs-grid">
                {lookupData.directs.map((uid, i) => (
                  <span key={i} className="lookup-direct-id" onClick={() => { setLookupId(String(uid)); }}>ID: {uid}</span>
                ))}
              </div>
            ) : (
              <span className="admin-loading">No direct referrals</span>
            )}
          </div>
        )}
      </AdminSection>

      {/* ===== CONTRACT STATUS - both owner and admin can view ===== */}
      <AdminSection title="Contract Status">
        {contractStatus ? (
          <div className="admin-status-grid">
            <StatusRow label="Core → Pools" ok={contractStatus.corePoolsOk} />
            <StatusRow label="Core → Matrix" ok={contractStatus.coreMatrixOk} />
            <StatusRow label="Pools → Core" ok={contractStatus.poolsCoreOk} />
            <StatusRow label="Matrix → Core" ok={contractStatus.matrixCoreOk} />
            <StatusRow label="Views → Core" ok={contractStatus.viewsCoreOk} />
            <StatusRow label="Views → Pools" ok={contractStatus.viewsPoolsOk} />
          </div>
        ) : <span className="admin-loading">Loading...</span>}

        {isOwner && (
          <>
            <div className="admin-authorize-section">
              <span className="admin-sub-title">Quick Authorize (auto from config)</span>
              <div className="admin-btn-row">
                <AdminBtn onClick={() => exec('Set Pools on Core', () => contracts.core.setPoolsContract(CONTRACT_ADDRESSES.UVW_Pools))} disabled={!!processing || (contractStatus && contractStatus.corePoolsOk)}>Core→Pools {contractStatus?.corePoolsOk ? ' ✓' : ''}</AdminBtn>
                <AdminBtn onClick={() => exec('Set Matrix on Core', () => contracts.core.setMatrixContract(CONTRACT_ADDRESSES.UVW_Matrix))} disabled={!!processing || (contractStatus && contractStatus.coreMatrixOk)}>Core→Matrix {contractStatus?.coreMatrixOk ? ' ✓' : ''}</AdminBtn>
              </div>
              <div className="admin-btn-row">
                <AdminBtn onClick={() => exec('Set Core on Pools', () => contracts.pools.setCoreContract(CONTRACT_ADDRESSES.UVW_Core))} disabled={!!processing || (contractStatus && contractStatus.poolsCoreOk)}>Pools→Core {contractStatus?.poolsCoreOk ? ' ✓' : ''}</AdminBtn>
                <AdminBtn onClick={() => exec('Set Core on Matrix', () => contracts.matrix.setCoreContract(CONTRACT_ADDRESSES.UVW_Core))} disabled={!!processing || (contractStatus && contractStatus.matrixCoreOk)}>Matrix→Core {contractStatus?.matrixCoreOk ? ' ✓' : ''}</AdminBtn>
              </div>
              <div className="admin-btn-row">
                <AdminBtn onClick={() => exec('Set Core on Views', () => contracts.views.setCoreContract(CONTRACT_ADDRESSES.UVW_Core))} disabled={!!processing || (contractStatus && contractStatus.viewsCoreOk)}>Views→Core {contractStatus?.viewsCoreOk ? ' ✓' : ''}</AdminBtn>
                <AdminBtn onClick={() => exec('Set Pools on Views', () => contracts.views.setPoolsContract(CONTRACT_ADDRESSES.UVW_Pools))} disabled={!!processing || (contractStatus && contractStatus.viewsPoolsOk)}>Views→Pools {contractStatus?.viewsPoolsOk ? ' ✓' : ''}</AdminBtn>
              </div>
            </div>

            <div style={{ marginTop: 12 }}>
              <AdminInput label="Manual Contract Address (override)" value={setContractAddr} onChange={setSetContractAddr} placeholder="0x..." />
              <div className="admin-btn-row">
                <AdminBtn onClick={() => exec('Set Pools on Core', () => contracts.core.setPoolsContract(setContractAddr))} disabled={!!processing || !setContractAddr}>Core→Pools</AdminBtn>
                <AdminBtn onClick={() => exec('Set Matrix on Core', () => contracts.core.setMatrixContract(setContractAddr))} disabled={!!processing || !setContractAddr}>Core→Matrix</AdminBtn>
              </div>
            </div>
          </>
        )}
      </AdminSection>

      {/* ===== EMERGENCY WITHDRAW - both owner and admin ===== */}
      <AdminSection title="Emergency Withdraw">
        <span className="admin-sub-title">Withdraw USDT from contract balance to your wallet.</span>
        <AdminInput label="Amount (USDT)" value={emergAmt} onChange={setEmergAmt} placeholder="Amount" type="number" />
        <AdminBtn onClick={() => exec('Emergency Withdraw', () => contracts.core.emergencyWithdraw(ethers.utils.parseUnits(emergAmt || '0', 18)))} disabled={!!processing || !emergAmt} danger>
          Emergency Withdraw
        </AdminBtn>
      </AdminSection>

      {/* ===== USER MANAGEMENT ===== */}
      <AdminSection title="User Management">
        {isOwner && (
          <>
            <AdminInput label="Wallet Address" value={regWallet} onChange={setRegWallet} placeholder="0x..." />
            <AdminInput label="Referrer ID" value={regRef} onChange={setRegRef} placeholder="Referrer ID" type="number" />
            <AdminBtn onClick={() => exec('Register Privileged', () => contracts.core.adminRegisterPrivileged(regWallet, regRef || 0))} disabled={!!processing}>Register Privileged (All 7 Packages)</AdminBtn>
          </>
        )}

        <div style={{ marginTop: 12 }}>
          {!isOwner && (
            <>
              <AdminInput label="Wallet Address" value={regWallet} onChange={setRegWallet} placeholder="0x..." />
              <AdminInput label="Referrer ID" value={regRef} onChange={setRegRef} placeholder="Referrer ID" type="number" />
            </>
          )}
          <label className="admin-label">Register Virtual - Up To Package</label>
          <div className="admin-pkg-select">
            {PACKAGE_NAMES.map((name, i) => (
              <button key={i} className={`admin-pkg-opt ${parseInt(regPkgId) === i ? 'admin-pkg-opt-active' : ''}`} onClick={() => setRegPkgId(String(i))}>
                {name}
              </button>
            ))}
          </div>
          <AdminBtn onClick={() => exec('Register Virtual', () => contracts.core.adminRegisterVirtual(regWallet, regRef || 0, parseInt(regPkgId) || 0))} disabled={!!processing}>Register Virtual</AdminBtn>
        </div>

        <div style={{ marginTop: 12 }}>
          <AdminInput label="User ID to Block" value={blockUserId} onChange={setBlockUserId} placeholder="User ID" type="number" />
          <div className="admin-btn-row">
            <AdminBtn onClick={() => exec('Block User', () => contracts.core.blockUser(blockUserId, true))} disabled={!!processing} danger>Block</AdminBtn>
            <AdminBtn onClick={() => exec('Unblock User', () => contracts.core.blockUser(blockUserId, false))} disabled={!!processing}>Unblock</AdminBtn>
          </div>
        </div>

        <div style={{ marginTop: 12 }}>
          <AdminInput label="User ID" value={retopUserId} onChange={setRetopUserId} placeholder="User ID" type="number" />
          <label className="admin-label">Retopup Package</label>
          <div className="admin-pkg-select">
            {PACKAGE_NAMES.map((name, i) => (
              <button key={i} className={`admin-pkg-opt ${parseInt(retopPkgId) === i ? 'admin-pkg-opt-active' : ''}`} onClick={() => setRetopPkgId(String(i))}>
                {name}
              </button>
            ))}
          </div>
          <AdminBtn onClick={() => exec('Admin Retopup', () => contracts.core.adminRetopup(retopUserId, retopPkgId))} disabled={!!processing}>Admin Retopup (No Funds)</AdminBtn>
        </div>
      </AdminSection>

      {/* ===== ROYALTY MANAGEMENT ===== */}
      <AdminSection title="Royalty Management">
        {royaltyData && (
          <div className="admin-royalty-stats">
            <div className="admin-royalty-pool">
              <span className="admin-royalty-title">Elite Royalty Pool</span>
              <div className="admin-royalty-grid">
                <div className="admin-royalty-item"><span className="admin-royalty-label">Accumulated</span><span className="admin-royalty-val">${formatUSDT(royaltyData.eliteAccum)}</span></div>
                <div className="admin-royalty-item"><span className="admin-royalty-label">Eligible Users</span><span className="admin-royalty-val">{royaltyData.eliteEligible}</span></div>
                <div className="admin-royalty-item"><span className="admin-royalty-label">Per User</span><span className="admin-royalty-val" style={{ color: '#ffd700' }}>${formatUSDT(royaltyData.elitePerUser)}</span></div>
                <div className="admin-royalty-item"><span className="admin-royalty-label">Current Round</span><span className="admin-royalty-val">{royaltyData.eliteRound}</span></div>
              </div>
            </div>
            <div className="admin-royalty-pool">
              <span className="admin-royalty-title">Global Royalty Pool</span>
              <div className="admin-royalty-grid">
                <div className="admin-royalty-item"><span className="admin-royalty-label">Accumulated</span><span className="admin-royalty-val">${formatUSDT(royaltyData.globalAccum)}</span></div>
                <div className="admin-royalty-item"><span className="admin-royalty-label">Eligible Users</span><span className="admin-royalty-val">{royaltyData.globalEligible}</span></div>
                <div className="admin-royalty-item"><span className="admin-royalty-label">Per User</span><span className="admin-royalty-val" style={{ color: '#ffd700' }}>${formatUSDT(royaltyData.globalPerUser)}</span></div>
                <div className="admin-royalty-item"><span className="admin-royalty-label">Current Round</span><span className="admin-royalty-val">{royaltyData.globalRound}</span></div>
              </div>
            </div>
          </div>
        )}

        <AdminInput label="Elite Round Amount (USDT)" value={eliteRoundAmt} onChange={setEliteRoundAmt} placeholder="Amount per user" type="number" />
        <AdminBtn onClick={() => exec('Set Elite Round', () => contracts.pools.setEliteRoundIncome(ethers.utils.parseUnits(eliteRoundAmt || '0', 18)))} disabled={!!processing || !eliteRoundAmt}>Set Elite Round Income</AdminBtn>

        <div style={{ marginTop: 12 }}>
          <AdminInput label="Global Round Amount (USDT)" value={globalRoundAmt} onChange={setGlobalRoundAmt} placeholder="Amount per user" type="number" />
          <AdminBtn onClick={() => exec('Set Global Round', () => contracts.pools.setGlobalRoundIncome(ethers.utils.parseUnits(globalRoundAmt || '0', 18)))} disabled={!!processing || !globalRoundAmt}>Set Global Round Income</AdminBtn>
        </div>

        <div className="admin-btn-row" style={{ marginTop: 12 }}>
          <AdminBtn onClick={() => exec('Reset Elite', () => contracts.pools.resetEliteAccumulation())} disabled={!!processing} danger>Reset Elite</AdminBtn>
          <AdminBtn onClick={() => exec('Reset Global', () => contracts.pools.resetGlobalAccumulation())} disabled={!!processing} danger>Reset Global</AdminBtn>
        </div>

        {isOwner && (
          <div style={{ marginTop: 12 }}>
            <AdminInput label="User ID for Eligibility Override" value={eligUserId} onChange={setEligUserId} placeholder="User ID" type="number" />
            <div className="admin-btn-row">
              <AdminBtn onClick={() => exec('Set Elite Eligible', () => contracts.pools.adminSetEliteEligibility(eligUserId, true))} disabled={!!processing}>Elite On</AdminBtn>
              <AdminBtn onClick={() => exec('Remove Elite', () => contracts.pools.adminSetEliteEligibility(eligUserId, false))} disabled={!!processing} danger>Elite Off</AdminBtn>
              <AdminBtn onClick={() => exec('Set Global Eligible', () => contracts.pools.adminSetGlobalEligibility(eligUserId, true))} disabled={!!processing}>Global On</AdminBtn>
              <AdminBtn onClick={() => exec('Remove Global', () => contracts.pools.adminSetGlobalEligibility(eligUserId, false))} disabled={!!processing} danger>Global Off</AdminBtn>
            </div>
          </div>
        )}
      </AdminSection>

      {/* ===== REWARD ELIGIBLE USERS ===== */}
      <AdminSection title="Reward Eligible Users">
        <span className="admin-sub-title">Eligible users discovered automatically as members browse + cached locally. Use Scan All to refresh from chain.</span>

        <label className="admin-label">Select Reward Tier</label>
        <div className="admin-pkg-select" style={{ marginBottom: 10 }}>
          {rewardTiers.length > 0 ? rewardTiers.map(t => (
            <button key={t.id} className={`admin-pkg-opt ${parseInt(selectedRewardTier) === t.id ? 'admin-pkg-opt-active' : ''}`}
              onClick={() => setSelectedRewardTier(String(t.id))}>
              {t.name}
            </button>
          )) : <span className="admin-loading">Loading tiers...</span>}
        </div>

        <div className="admin-btn-row">
          <AdminBtn onClick={loadRewardEligibleUsers} disabled={rewardLoading || rewardTiers.length === 0}>
            {rewardLoading ? 'Loading...' : 'Load Eligible'}
          </AdminBtn>
          <AdminBtn onClick={scanAllUsersForRewards} disabled={rewardLoading}>
            {rewardLoading ? '...' : 'Scan All Users'}
          </AdminBtn>
          <AdminBtn onClick={() => { clearEligibility(); setRewardEligibleByTier({}); notify.info('Cache cleared'); }} disabled={rewardLoading} danger>
            Clear Cache
          </AdminBtn>
        </div>

        {rewardEligibleByTier[selectedRewardTier] && (
          <div className="admin-reward-tier-list">
            <span className="admin-reward-tier-title">
              {rewardTiers[parseInt(selectedRewardTier)]?.name}: {rewardEligibleByTier[selectedRewardTier].length} eligible
            </span>
            {rewardEligibleByTier[selectedRewardTier].length === 0 ? (
              <span className="admin-loading">No eligible users yet</span>
            ) : (
              <div className="admin-eligible-list">
                {rewardEligibleByTier[selectedRewardTier].map(u => (
                  <div key={u.userId} className="admin-eligible-row">
                    <div className="admin-eligible-info">
                      <span className="admin-eligible-uid">ID: {u.userId}</span>
                      <span className="admin-eligible-progress">{u.count}/{u.required}</span>
                    </div>
                    {u.given ? (
                      <span className="admin-eligible-given-badge">✓ Given</span>
                    ) : (
                      <button className="admin-btn" style={{ width: 'auto', padding: '6px 12px', fontSize: 12, margin: 0 }}
                        disabled={processing === 'markGiven-' + u.userId}
                        onClick={() => markAsGiven(parseInt(selectedRewardTier), u.userId)}>
                        {processing === 'markGiven-' + u.userId ? '...' : 'Mark Given'}
                      </button>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        <div style={{ marginTop: 12 }}>
          <span className="admin-sub-title">Manual Mark Given (by ID)</span>
          <AdminInput label="Tier ID" value={markGivenTier} onChange={setMarkGivenTier} placeholder="Tier ID" type="number" />
          <AdminInput label="User ID" value={markGivenUser} onChange={setMarkGivenUser} placeholder="User ID" type="number" />
          <AdminBtn onClick={() => exec('Mark Reward Given', () => contracts.pools.markRewardGiven(markGivenTier, markGivenUser))} disabled={!!processing || !markGivenTier || !markGivenUser}>Mark As Given</AdminBtn>
        </div>
      </AdminSection>

      {/* ===== ADMIN CLAIM FOR PRIVILEGED ===== */}
      <AdminSection title="Claim For Privileged Users">
        <span className="admin-sub-title">Triggers claim for type-1 (privileged) users. Income goes to team wallets.</span>
        {isOwner ? (
          <>
            <AdminInput label="User IDs (comma separated)" value={privClaimIds} onChange={setPrivClaimIds} placeholder="e.g. 1234, 5678, 9012" />
            <div className="admin-btn-row">
              <AdminBtn onClick={savePrivClaimIds} disabled={!privClaimIds}>Save IDs</AdminBtn>
              <AdminBtn onClick={handlePrivClaim} disabled={!!processing || !privClaimIds}>
                {processing === 'privClaim' ? 'Processing...' : 'Claim Now'}
              </AdminBtn>
            </div>
            {privClaimSavedIds && (
              <div className="admin-saved-info">
                <span>Saved: {privClaimSavedIds.split(',').filter(s => s.trim()).length} IDs</span>
              </div>
            )}
          </>
        ) : (
          <>
            {privClaimSavedIds ? (
              <>
                <div className="admin-saved-info">
                  <span>Configured users: <strong>{privClaimSavedIds.split(',').filter(s => s.trim()).length}</strong></span>
                </div>
                <AdminBtn onClick={handlePrivClaim} disabled={!!processing}>
                  {processing === 'privClaim' ? 'Claiming...' : 'Claim For All Privileged Users'}
                </AdminBtn>
              </>
            ) : (
              <span className="admin-loading"></span>
            )}
          </>
        )}
      </AdminSection>

      {/* ===== CHANGE ADMIN - Owner only ===== */}
      {isOwner && (
        <AdminSection title="Change Admin Wallet">
          <AdminInput label="New Admin Address" value={newAdminAddr} onChange={setNewAdminAddr} placeholder="0x..." />
          <span className="admin-sub-title">Updates admin on all 3 contracts (Core, Pools, Matrix)</span>
          <AdminBtn onClick={handleChangeAdmin} disabled={!!processing || !newAdminAddr} danger>
            {processing === 'changeAdmin' ? 'Processing...' : 'Change Admin on All Contracts'}
          </AdminBtn>
        </AdminSection>
      )}

      {/* ===== WALLET MANAGEMENT - Owner only ===== */}
      {isOwner && (
        <AdminSection title="Wallet Management">
          <AdminInput label="New Wallet Address" value={walletAddr} onChange={setWalletAddr} placeholder="0x..." />
          <div className="admin-btn-grid">
            {['TeamWallet1', 'TeamWallet2', 'NetworkWallet', 'SalaryWallet1', 'SalaryWallet2', 'ExpenseWallet', 'RewardWallet'].map(name => (
              <AdminBtn key={name} onClick={() => exec(`Set ${name}`, () => contracts.core[`set${name}`](walletAddr))} disabled={!!processing || !walletAddr}>{name}</AdminBtn>
            ))}
          </div>
        </AdminSection>
      )}

      {/* ===== SYSTEM - Owner only ===== */}
      {isOwner && (
        <AdminSection title="System">
          {/* (router-related claims removed) */}
          <div style={{ marginTop: 12 }}>
            <AdminInput label="Default Referrer ID" value={defaultRefId} onChange={setDefaultRefId} placeholder="User ID" type="number" />
            <AdminBtn onClick={() => exec('Set Default Referrer', () => contracts.core.setDefaultReferrer(defaultRefId))} disabled={!!processing || !defaultRefId}>Set Referrer</AdminBtn>
          </div>
          <div style={{ marginTop: 12 }}>
            <AdminInput label="USDT Token Address" value={usdtAddr} onChange={setUsdtAddr} placeholder="0x..." />
            <AdminBtn onClick={() => exec('Set USDT Token', () => contracts.core.setUSDTToken(usdtAddr))} disabled={!!processing || !usdtAddr}>Set USDT</AdminBtn>
          </div>
        </AdminSection>
      )}

      {/* ===== REWARD TIERS - Owner only ===== */}
      {isOwner && (
        <AdminSection title="Reward Tiers Configuration">
          <AdminInput label="Tier ID" value={rewardTierId} onChange={setRewardTierId} placeholder="0, 1, 2..." type="number" />
          <AdminInput label="Name" value={rewardName} onChange={setRewardName} placeholder="Reward name" />
          <AdminInput label="Description" value={rewardDesc} onChange={setRewardDesc} placeholder="Description" />
          <AdminInput label="Directs Required" value={rewardDirects} onChange={setRewardDirects} placeholder="0" type="number" />
          <AdminInput label="Level Required" value={rewardLevel} onChange={setRewardLevel} placeholder="0" type="number" />
          <AdminInput label="Members Required" value={rewardMembers} onChange={setRewardMembers} placeholder="0" type="number" />
          <AdminBtn onClick={() => exec('Set Reward Tier', () => contracts.pools.setRewardTier(
            rewardTierId, rewardName, rewardDesc, rewardDirects || 0, rewardLevel || 0, rewardMembers || 0, true, true
          ))} disabled={!!processing}>Save Reward Tier</AdminBtn>
          <div className="admin-btn-row" style={{ marginTop: 8 }}>
            <AdminBtn onClick={() => exec('Activate Tier', () => contracts.pools.setRewardTierActive(rewardTierId, true))} disabled={!!processing}>Activate</AdminBtn>
            <AdminBtn onClick={() => exec('Deactivate Tier', () => contracts.pools.setRewardTierActive(rewardTierId, false))} disabled={!!processing} danger>Deactivate</AdminBtn>
          </div>
        </AdminSection>
      )}
    </div>
  );
}
